import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { ContentCopyOutlinedIcon, DeleteOutlinedIcon, EditOutlinedIcon, VisibilityOutlinedIcon } from 'tuui';
import { useNavigate } from 'hooks';
import { useGridContext } from 'modules/campaigns/modules/campaigns-reports/components/GridContextProvider/GridContextProvider';
import {
    isAppInstall,
    isCreativeResubmittable,
    isDeleteCreativeAllowed,
    isDuplicateCreativeAllowed,
    isEditCreativeAllowed,
    isMotionAd,
    isPreviewCreativeAllowed,
} from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication/hooks';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useModal } from 'modules/taboola-common-frontend-modules/modals';
import ActionsCellRenderer from './ActionsCellRenderer';

const ACTIONS = {
    EDIT: 'edit',
    DUPLICATE: 'duplicate',
    PREVIEW: 'preview',
    DELETE: 'delete',
};

const AD_TYPE = {
    MOTION_AD: 'video',
    APP_INSTALL: 'appinstall',
    UNRESUBMITTABLE: 'unresubmittable',
    GENERAL: 'general',
};

const getTooltipMessage = (data, action) => {
    const motionAdMessageId = isMotionAd(data) ? AD_TYPE.MOTION_AD : null;
    const appInstallMessageId = isAppInstall(data) ? AD_TYPE.APP_INSTALL : null;
    const unresubmittableMessageId =
        !isCreativeResubmittable(data) && action === ACTIONS.EDIT ? AD_TYPE.UNRESUBMITTABLE : null;
    const domain = appInstallMessageId || motionAdMessageId || unresubmittableMessageId || AD_TYPE.GENERAL;
    const messageId = `app.campaigns.creativeReports.actions.${action}.creative-library.${domain}`;
    return <FormattedMessage id={messageId} />;
};

const getTooltipProps = (data, action) => {
    if (!isCreativeResubmittable(data) && action === ACTIONS.EDIT) {
        return { interactive: true };
    }
    return null;
};

const getModalMessages = () => ({
    title: (
        <FormattedMessage
            id="app.campaigns.creativeReports.actions.delete.creative-library.header"
            tagName="h2"
            defaultMessage="Delete Confirmation"
        />
    ),
    content: (
        <FormattedMessage
            id="creative-library.modal.content.delete"
            defaultMessage="Deleting this creative will permanently stop it from serving. All historical reporting data will be preserved. This action is irreversible."
        />
    ),
    formProps: {
        submitButtonText: (
            <FormattedMessage id="creative-library.modal.button.delete" defaultMessage="Delete Creative" />
        ),
    },
});

export const CreativeLibraryActionsCellRenderer = ({
    data,
    onEdit,
    onPreview,
    onDelete,
    onDuplicate,
    context,
    ...rest
}) => {
    const { open: openModal } = useModal();
    const dispatch = useDispatch();
    const hasEditPermission = usePermissions('CREATIVE_EDIT');
    const { isGridEditDisabled } = useGridContext();
    const navigate = useNavigate();

    const shouldDisableDuplicateButton =
        !isDuplicateCreativeAllowed(data) || !hasEditPermission || isGridEditDisabled || isAppInstall(data);

    const deleteHandler = async data => {
        const result = await openModal(getModalMessages());
        if (result) {
            dispatch(onDelete(data));
        }
    };

    const buttonConfigs = [
        {
            onClick: () => onEdit({ data, context: { ...context, navigate } }),
            isDisabled: !isEditCreativeAllowed(data) || !hasEditPermission || isGridEditDisabled,
            dataMetricComponent: 'EditActionButton',
            icon: <EditOutlinedIcon />,
            iconLabel: 'edit',
            tooltipContent: getTooltipMessage(data, ACTIONS.EDIT),
            tooltipProps: getTooltipProps(data, ACTIONS.EDIT),
        },
        {
            onClick: () => dispatch(onDuplicate(data, navigate)),
            isDisabled: shouldDisableDuplicateButton,
            dataMetricComponent: 'DuplicateActionButton',
            icon: <ContentCopyOutlinedIcon />,
            iconLabel: 'duplicate',
            tooltipContent: getTooltipMessage(data, ACTIONS.DUPLICATE),
        },
        {
            onClick: () => onPreview({ data, context: { ...context, navigate } }),
            isDisabled: !isPreviewCreativeAllowed(data),
            dataMetricComponent: 'PreviewActionButton',
            icon: <VisibilityOutlinedIcon />,
            iconLabel: 'preview',
            permissionsGuardProps: {
                permissions: 'CREATIVE_PREVIEW_VIEW',
            },
            tooltipContent: getTooltipMessage(data, ACTIONS.PREVIEW),
        },
        {
            onKeyDown: e => e.preventDefault(),
            onClick: () => deleteHandler(data),
            isDisabled: !isDeleteCreativeAllowed(data) || !hasEditPermission,
            dataMetricComponent: 'DeleteActionButton',
            icon: <DeleteOutlinedIcon />,
            iconLabel: 'delete',
            tooltipContent: getTooltipMessage(data, ACTIONS.DELETE),
        },
    ];

    return <ActionsCellRenderer data={data} buttonConfigs={buttonConfigs} context={context} {...rest} />;
};

CreativeLibraryActionsCellRenderer.propTypes = {
    /** row data which is injected by the grid */
    data: PropTypes.object,
    onDelete: PropTypes.func,
    onPreview: PropTypes.func,
    ...ActionsCellRenderer.propTypes,
};
